import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import { getCard, getGap } from '@Components/BigBrowse/Model/Classes/GridCard';
import type {
  CameraProps,
  GridCardData,
  GridLayout,
  IScrollTarget,
  PlaneSize,
  Position2D,
} from '@Components/BigBrowse/Model/ModelTypes';
import { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import type { FloorContentCellDM, FloorContentDM } from '@Model/FloorModels';

import type { FloorDisplayVM } from './FloorCellLayoutViewModel';
import {
  getDisplaySizeWithMargins,
  getFloorPathwaySize,
  getHeroCardSize,
  getHeroPosition,
  getProductCardSize,
  getProductPositionByIndex,
} from './FloorCellViewSizers';

const FILTER_OUT_GAPS = true;

export const getFloorCellLayout = (
  data: FloorContentDM,
  cameraProps: CameraProps,
  collapsedView: boolean
): GridLayout<FloorContentDM> => {
  const size = getFloorSize(data, collapsedView);
  const models = getDisplayViewModels(data, size, cameraProps, collapsedView);
  const nonEmptyDisplays = models.filter((m) => Boolean(m.dm.displayId));

  const displays = models.map((model) => ({
    model,
    position: model.worldPosition,
    box: {
      top: model.worldPosition.y + model.size.height / 2,
      bottom: model.worldPosition.y - model.size.height / 2,
      left: model.worldPosition.x - model.size.width / 2,
      right: model.worldPosition.x + model.size.width / 2,
    },
  }));

  const items = getItems(nonEmptyDisplays, collapsedView);
  const filteredItems = FILTER_OUT_GAPS
    ? items.filter((item) => item.type !== PlaneTypes.Gap)
    : items;

  return {
    size,
    items: filteredItems,
    scrollTargets: getScrollTargetsFromModels(models),
    modelData: data,
    displays,
  };
};

const getScrollTargetsFromModels = (
  models: FloorDisplayVM[]
): IScrollTarget => {
  const scrollTargets: IScrollTarget = {};
  models.forEach((model) => {
    if (model.dm.isFocusPoint) {
      scrollTargets[model.dm.departmentId] =
        model.hero.worldPosition ?? model.worldPosition;
    }
  });
  return scrollTargets;
};

const getFloorSize = (
  data: FloorContentDM,
  collapsedView?: boolean
): PlaneSize => {
  const displaySize = getDisplaySizeWithMargins(collapsedView ?? false);

  return {
    width:
      data.width * displaySize.width +
      // Add Floor Pathways spacing
      getFloorPathwaySize().vertical * data.pathwaysVertical.length,
    height:
      data.height * displaySize.height +
      getFloorPathwaySize().horizontal * data.pathwaysHorizontal.length,
  };
};

const getDisplayViewModels = (
  data: FloorContentDM,
  floorSize: PlaneSize,
  cameraProps: CameraProps,
  collapsedView: boolean
  // eslint-disable-next-line max-params
): FloorDisplayVM[] =>
  data.cells.map((cell) =>
    getDisplayViewModel({
      cell,
      floorSize,
      floorContent: data,
      cameraProps,
      collapsedView,
    })
  );

const getDisplayViewModel = ({
  cell,
  floorSize,
  floorContent,
  cameraProps,
  collapsedView,
}: {
  cell: FloorContentCellDM;
  floorSize: PlaneSize;
  floorContent: FloorContentDM;
  cameraProps: CameraProps;
  collapsedView: boolean;
}): FloorDisplayVM => {
  const displaySize = getDisplaySizeWithMargins(collapsedView);

  const worldPosition = getDisplayWorldPosition({
    displaySize,
    floorSize,
    cell,
    floorContent,
  });

  const hero = getDisplayHeroCard({
    floorContent,
    cell,
    displayWorldPosition: worldPosition,
    cameraProps,
    collapsedView,
  });

  return {
    dm: cell,
    size: displaySize,
    worldPosition,
    hero,
    products: getDisplayProductCards({
      floorContent,
      cell,
      displayWorldPosition: worldPosition,
      parentHero: hero,
      collapsedView,
    }),
  };
};

function getDisplayWorldPosition({
  displaySize,
  floorSize,
  cell,
  floorContent,
}: {
  displaySize: PlaneSize;
  floorSize: PlaneSize;
  cell: FloorContentCellDM;
  floorContent: FloorContentDM;
}): Position2D {
  // Here we consider Floor 0,0 to be top left corner
  // X increases from left to right
  // y increases from bottom to top
  // But the `cell` coordination system is
  // 0,0 top-left and increase left to right, top to bottom

  const { pathwaysHorizontal, pathwaysVertical } = floorContent;

  // Filtering out how many pathways were added before the item
  // And add an extra spacing (the total width of added pathways) to the item's position
  const verticalMarginFactor = pathwaysHorizontal.filter(
    (val) => val <= cell.y
  ).length;
  const horizontalMarginFactor = pathwaysVertical.filter(
    (val) => val <= cell.x
  ).length;

  const spacingVertical = getFloorPathwaySize().vertical * verticalMarginFactor;
  const spacingHorizontal =
    getFloorPathwaySize().horizontal * horizontalMarginFactor;

  const topLeftAnchored: Position2D = {
    x: cell.x * displaySize.width + spacingHorizontal,
    y: -cell.y * displaySize.height - spacingVertical,
  };

  // Floor 0,0 is top-left corner, but display is anchored to its center
  const centerAnchored: Position2D = {
    x: topLeftAnchored.x + displaySize.width / 2,
    y: topLeftAnchored.y - displaySize.height / 2,
  };

  // Finally we convert to world coordinates with 0,0 in the center of the floor
  return {
    x: -floorSize.width / 2 + centerAnchored.x,
    y: floorSize.height / 2 + centerAnchored.y,
  };
}
function getItems(
  models: FloorDisplayVM[],
  collapsedView?: boolean
): GridCard<PlaneTypes>[] {
  return models
    .map((model) => {
      const products = collapsedView ? [] : model.products;

      return [model.hero, ...products];
    })
    .flat();
}

function getDisplayHeroCard({
  floorContent,
  cell,
  displayWorldPosition,
  collapsedView,
}: {
  floorContent: FloorContentDM;
  cell: FloorContentCellDM;
  displayWorldPosition: Position2D;
  cameraProps: CameraProps;
  collapsedView: boolean;
}): GridCard<PlaneTypes> {
  const heroSide = getHeroCardSize(collapsedView);
  const key = getKey(cell, getHeroPlaneType(floorContent));

  // if the cell is empty (no display) we return a gap card, so it not renders
  // We just need it as a target for the department scroll
  const heroPlaneType = cell.displayId
    ? getHeroPlaneType(floorContent)
    : PlaneTypes.Gap;

  const card = getCard({
    data: getGridCardDataFromCell(cell, key),
    type: heroPlaneType,
    size: { width: heroSide, height: heroSide },
    position: getHeroPosition(collapsedView),
    parent: { worldPosition: displayWorldPosition },
    blockHover: getBlockHover(),
  });
  card.key = key;

  return card;
}

function getGridCardDataFromCell(
  cell: FloorContentCellDM,
  key: string
): GridCardData<PlaneTypes> {
  return {
    id: cell.displayId?.toString(),
    key,
    title: cell.displayTitle,
    body: cell.displaySubTitle,
    imageUrl: cell.heroImageUrl,
    destination: cell.destination,
    type: undefined,
  };
}

function getBlockHover(): boolean {
  return true; // TODO: implement this based on the type of floor (BrandConcession or ShopFloor)
}

function getDisplayProductCards({
  floorContent,
  cell,
  displayWorldPosition,
  parentHero,
  collapsedView,
}: {
  floorContent: FloorContentDM;
  cell: FloorContentCellDM;
  displayWorldPosition: Position2D;
  parentHero: GridCard<PlaneTypes>;
  collapsedView: boolean;
}): GridCard<PlaneTypes>[] {
  return (cell.products ?? []).map((product, productIndex) =>
    getDisplayProductCard({
      productIndex,
      product,
      cell,
      displayWorldPosition,
      floorContent,
      parentHero,
      collapsedView,
    })
  );
}

function getDisplayProductCard({
  floorContent,
  product,
  displayWorldPosition,
  cell,
  productIndex,
  parentHero,
  collapsedView,
}: {
  product: FloorContentCellDM['products'][0];
  cell: FloorContentCellDM;
  displayWorldPosition: Position2D;
  floorContent: FloorContentDM;
  productIndex: number;
  parentHero: GridCard<PlaneTypes>;
  collapsedView: boolean;
}): GridCard<PlaneTypes> {
  const side = getProductCardSize();
  const key = getProductKey(cell, getProductPlaneType(floorContent), product);

  const cardSize = { width: side, height: side };
  const position = getProductPositionByIndex(collapsedView)(productIndex);

  let card: GridCard<any> = getCard({
    data: getGridCardDataFromProduct(product, key),
    type: getProductPlaneType(floorContent),
    size: cardSize,
    position,
    parent: { worldPosition: displayWorldPosition },
    blockHover: getBlockHover(),
    parentHero,
  });

  card.key = key;
  card = replaceProductWithGap(card, cell, productIndex);

  return card;
}

// Replace Products with gaps if cell has gutters
function replaceProductWithGap(
  card: GridCard<any>,
  cell: FloorContentCellDM,
  productIndex: number
) {
  const topIndeces = [0, 1, 2];
  const rightIndeces = [2, 3, 4, 5];
  const bottomIndeces = [5, 6, 7];

  if (
    (cell.displayGutterStyle === 'Top' && topIndeces.includes(productIndex)) ||
    (cell.displayGutterStyle === 'Right' &&
      rightIndeces.includes(productIndex)) ||
    (cell.displayGutterStyle === 'Bottom' &&
      bottomIndeces.includes(productIndex))
  ) {
    return getGap(card.size, undefined, card.worldPosition);
  }
  return card;
}

function getHeroPlaneType(floorContent: FloorContentDM): PlaneTypes {
  return floorContent.layout === 'BrandConcession'
    ? PlaneTypes.Card_ShopFloor_Hero
    : PlaneTypes.Card_BigBrowse_Bookend;
}

function getKey(cell: FloorContentCellDM, type: PlaneTypes) {
  return `${cell.displayId}_${cell.departmentId}_${type}`;
}

function getProductKey(
  cell: FloorContentCellDM,
  type: PlaneTypes,
  product: FloorContentCellDM['products'][0]
): string {
  return `${cell.displayId}_${cell.departmentId}_${type}_${product.id}`;
}

function getProductPlaneType(floorContent: FloorContentDM): PlaneTypes {
  return floorContent.layout === 'BrandConcession'
    ? PlaneTypes.Card_ShopFloor_Product
    : PlaneTypes.Card_BigBrowse_Product;
}

function getGridCardDataFromProduct(
  product: FloorContentCellDM['products'][0],
  key: string
): GridCardData<PlaneTypes> {
  return {
    id: product.id.toString(),
    key,
    title: product.name,
    imageUrl: product.imageUrl,
    type: undefined,
  };
}
